/**
 * 通用Swiper组件
 * 可以被多个不同的部分使用
 * 
 * 使用方法:
 * 1. 在HTML中添加属性 data-swiper="true" 或使用特定的class
 * 2. 可选配置:
 *    - data-swiper-autoplay="true/false" 是否自动播放
 *    - data-swiper-loop="true/false" 是否循环
 *    - data-swiper-duration="5" 自动播放间隔(秒)
 *    - data-swiper-per-view="1" 每页显示数量
 *    - data-swiper-space="30" 间距(像素)
 *    - data-swiper-effect="slide/fade/cube/coverflow/flip" 切换效果
 *    - data-swiper-centered="true/false" 是否居中显示
 *    - data-swiper-speed="300" 过渡速度(毫秒)
 *    - data-swiper-breakpoints="768:1,950:3,1440:4" 响应式断点设置
 * 
 * 支持的选择器:
 * 1. 下面任一class均会被自动初始化:
 *    - .home-product-swiper
 *    - .product-review-showcase__swiper
 *    - .product-showcase__swiper
 *    - .common-swiper
 * 2. 或使用通用属性: [data-swiper="true"]
 */

class SwiperComponent {
  constructor() {
    // 支持多种选择器，包括通用选择器和特定组件选择器
    const selectors = [
      '[data-swiper="true"]',
      '.common-swiper'
    ];
    
    // 合并所有匹配的元素
    this.swiperContainers = [];
    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      if (elements.length > 0) {
        this.swiperContainers = [...this.swiperContainers, ...Array.from(elements)];
      }
    });
    
    // 去重（防止某些元素被多个选择器匹配）
    this.swiperContainers = [...new Set(this.swiperContainers)];
    
    if (this.swiperContainers.length > 0) {
      this.loadSwiperIfNeeded().then(() => {
        this.initAllSwipers();
      }).catch(error => {
        console.error('无法加载Swiper库', error);
      });
    }
  }
  
  /**
   * 如果需要，加载Swiper库
   */
  loadSwiperIfNeeded() {
    return new Promise((resolve, reject) => {
      if (typeof Swiper !== 'undefined') {
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/swiper@8/swiper-bundle.min.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
      
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/swiper@8/swiper-bundle.min.css';
      document.head.appendChild(link);
    });
  }
  
  /**
   * 初始化所有轮播
   */
  initAllSwipers() {
    this.swiperContainers.forEach(container => {
      // 如果是容器本身就是swiper元素，使用容器本身；否则查找其中的swiper元素
      const swiperElement = container.classList.contains('swiper') ? container : container.querySelector('.swiper');
      if (!swiperElement) return;
      
      // 获取配置
      const autoplay = container.dataset.swiperAutoplay === 'true' || container.classList.contains('home-product-swiper');
      const loop = container.dataset.swiperLoop !== 'false'; // 默认为true
      const duration = parseInt(container.dataset.swiperDuration || '5', 10) * 1000;
      const slidesPerView = parseInt(container.dataset.swiperPerView || '1', 10);
      const spaceBetween = parseInt(container.dataset.swiperSpace || '30', 10);
      const effect = container.dataset.swiperEffect || 'slide';
      const speed = parseInt(container.dataset.swiperSpeed || '300', 10);
      const centeredSlides = container.dataset.swiperCentered === 'true' || container.classList.contains('home-product-swiper');
      
      // 导航和分页器
      const paginationEl = swiperElement.querySelector('.swiper-pagination');
      const prevButtonEl = swiperElement.querySelector('.swiper-button-prev');
      const nextButtonEl = swiperElement.querySelector('.swiper-button-next');
      
      // 基本配置
      const swiperOptions = {
        slidesPerView: slidesPerView,
        spaceBetween: spaceBetween,
        loop: loop,
        speed: speed,
        autoHeight: true,
        grabCursor: true,
        effect: effect,
        centeredSlides: centeredSlides
      };
      
      // 响应式断点配置
      if (container.dataset.swiperBreakpoints) {
        try {
          const breakpointsData = container.dataset.swiperBreakpoints;
          const breakpointsObj = {};
          
          // 解析格式如 "768:1,950:3,1440:4"
          breakpointsData.split(',').forEach(point => {
            const [width, slides] = point.split(':');
            breakpointsObj[width.trim()] = {
              slidesPerView: parseInt(slides.trim(), 10),
              spaceBetween: spaceBetween
            };
          });
          
          swiperOptions.breakpoints = breakpointsObj;
        } catch (e) {
          console.error('断点配置格式错误', e);
        }
      }
      
      // 针对特定组件的默认断点配置
      if (container.classList.contains('home-product-swiper') && !swiperOptions.breakpoints) {
        swiperOptions.breakpoints = {
          768: {
            slidesPerView: 1,
            spaceBetween: 20
          },
          950: {
            slidesPerView: 3,
            spaceBetween: 30
          },
          1440: {
            slidesPerView: 4,
            spaceBetween: 40
          }
        };
      }
      
      // 获取并检查幻灯片
      const slides = container.querySelectorAll('.swiper-slide');
      const slidesCount = slides.length;
      
      // 判断是否应该显示分页器
      const shouldShowPagination = slidesCount > slidesPerView;
      
      // 如果不应该显示分页器，直接隐藏元素
      if (paginationEl && !shouldShowPagination) {
        paginationEl.style.display = 'none';
      }
      
      // 只有在需要分页器时才添加分页器配置
      if (paginationEl && shouldShowPagination) {
        swiperOptions.pagination = {
          el: paginationEl,
          clickable: true
        };
      } else {
        // 完全不启用分页器
        swiperOptions.pagination = false;
      }
      
      // 处理循环模式
      // 如果幻灯片数量小于 slidesPerView * 2，则禁用循环模式以避免警告
      if (slidesCount < slidesPerView * 2) {
        swiperOptions.loop = false;
      }
      
      // 添加导航按钮
      if (prevButtonEl && nextButtonEl) {
        swiperOptions.navigation = {
          prevEl: prevButtonEl,
          nextEl: nextButtonEl
        };
      }
      
      // 添加自动播放
      if (autoplay) {
        swiperOptions.autoplay = {
          delay: duration,
          disableOnInteraction: false
        };
      }
      
      // 特殊效果配置
      if (effect === 'fade') {
        swiperOptions.fadeEffect = { crossFade: true };
      } else if (effect === 'coverflow') {
        swiperOptions.coverflowEffect = {
          rotate: 50,
          stretch: 0,
          depth: 100,
          modifier: 1,
          slideShadows: true
        };
      } else if (effect === 'cube') {
        swiperOptions.cubeEffect = {
          shadow: true,
          slideShadows: true,
          shadowOffset: 20,
          shadowScale: 0.94
        };
      } else if (effect === 'flip') {
        swiperOptions.flipEffect = {
          slideShadows: true
        };
      }
      
      // 创建Swiper实例
      try {
        const swiper = new Swiper(swiperElement, swiperOptions);
        
        // 检查分页器是否应该显示（当滑块数量小于等于每页显示数量时隐藏）
        if (paginationEl) {
          const slides = swiperElement.querySelectorAll('.swiper-slide');
          if (slides.length <= slidesPerView) {
            paginationEl.style.display = 'none';
          }
          
          // 程序化检测，在断点变化时也要检查
          window.addEventListener('resize', () => {
            let currentSlidesPerView = slidesPerView;
            
            // 根据断点获取当前的slidesPerView
            if (swiperOptions.breakpoints) {
              const width = window.innerWidth;
              Object.keys(swiperOptions.breakpoints).forEach(breakpoint => {
                if (width >= parseInt(breakpoint, 10)) {
                  currentSlidesPerView = swiperOptions.breakpoints[breakpoint].slidesPerView;
                }
              });
            }
            
            // 如果滑块数量小于等于当前每页显示数量，隐藏分页器
            if (slides.length <= currentSlidesPerView) {
              paginationEl.style.display = 'none';
            } else {
              paginationEl.style.display = '';
            }
          });
        }
        
        // 添加鼠标悬停暂停功能
        if (autoplay) {
          swiperElement.addEventListener('mouseenter', () => {
            swiper.autoplay.stop();
          });
          
          swiperElement.addEventListener('mouseleave', () => {
            swiper.autoplay.start();
          });
        }
        
        // 将swiper实例存储到元素上，便于外部访问
        swiperElement._swiper = swiper;
      } catch (error) {
        console.error('Swiper初始化失败', error);
        this.createFallbackDisplay(swiperElement);
      }
    });
  }
  
  /**
   * 如果Swiper初始化失败，创建后备显示
   */
  createFallbackDisplay(swiperElement) {
    // 如果Swiper不可用，使用基本的CSS显示第一个幻灯片
    const slides = swiperElement.querySelectorAll('.swiper-slide');
    slides.forEach((slide, index) => {
      if (index === 0) {
        slide.style.display = 'block';
      } else {
        slide.style.display = 'none';
      }
    });
    
    // 隐藏导航和分页
    const prevButton = swiperElement.querySelector('.swiper-button-prev');
    const nextButton = swiperElement.querySelector('.swiper-button-next');
    const pagination = swiperElement.querySelector('.swiper-pagination');
    
    if (prevButton) prevButton.style.display = 'none';
    if (nextButton) nextButton.style.display = 'none';
    if (pagination) pagination.style.display = 'none';
  }
}

// 当DOM加载完成后初始化
document.addEventListener('DOMContentLoaded', () => {
  new SwiperComponent();
});

// Shopify主题编辑器支持
document.addEventListener('shopify:section:load', () => {
  new SwiperComponent();
});

// 导出组件以便可以手动初始化
if (typeof window !== 'undefined') {
  window.SwiperComponent = SwiperComponent;
}
